import {
  openModal,
  routeTo,
  routeBack,
  finish,
  partiallyUpdate
} from '../../../core/actions';
import FlowHeader from '../../../components/FlowHeader/FlowHeader';
import FooterSite from '../../../components/FooterSite/FooterSite';
import BaseLayout from '../../../components/BaseLayout/BaseLayout';
import SummitRewards from '../../../components/BaseLayout/SummitRewards';
import {
  hasURLTickers,
  isValidEmail,
  isValidSSN,
  isValidMaskedSSN,
  usernameValidation,
  passwordValidation,
  isValidDob,
  isValidBeneorSucessorDob,
  meetsTaxableMinimumInvestment,
  chose,
  chosenAddressValidation,
  isReviewMode,
  existingUserShouldGotoLogin,
  existingUserShouldGotoEnablement,
  isUsernameAlreadyTaken,
  choseResultingPath,
  userValidationFailed,
  isAuthenticated,
  guestUserLoginRequired,
  showUpdateToIClassModal,
  isLAResidentRouteTester
} from '../../shared/testers';

import TaxableProfileScreen from '../../../screens/ProfileScreen/TaxableProfileScreen';
import PersonalInformationScreen from '../../../screens/PersonalInformationScreen/PersonalInformationScreen';
import CreateCredentialsScreen from '../../../screens/CreateCredentialsScreen/CreateCredentialsScreen';
import ContactInformationScreen from '../../../screens/ContactInformationScreen/ContactInformationScreen';
import InvestmentOptionsScreen from '../../../screens/InvestmentOptionsScreen/InvestmentOptionsScreen';
import ReviewScreen from '../../../screens/ReviewScreen/ReviewScreen';
import BeneficiariesScreen from '../../../screens/BeneficiariesScreen/BeneficiariesScreen';
import ConfirmationScreen from '../../../screens/ConfirmationScreen/ConfirmationScreen';
import InvestmentReviewScreen from '../../../screens/InvestmentReviewScreen/InvestmentReviewScreen';
import BYOPScreen from '../../../screens/BYOPScreen/BYOPScreen';
import MoneyMarketScreen from '../../../screens/MoneyMarketScreen/MoneyMarketScreen';
import InvestmentOptionsContents from '../../../components/InvestmentOptions/TaxableContents';
import SkipBeneficiariesModal from '../../../components/SkipBeneficiariesModal/SkipBeneficiariesModal';
import TaxableChecklistScreen from '../../../screens/ChecklistScreen/TaxableChecklistScreen';
import UserValidationModal from '../../../components/UserValidationModal/UserValidationModal';
import makeForcedLoginModal from '../../../components/ForcedLoginModal/ForcedLoginModal';
import AddressValidationModal from '../../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../../components/CredentialsValidationModal/CredentialsValidationModal';
import UpdateToIClassModal from '../../../components/UpdateToIClass/UpdateToIClassModal';
import GuestUserLoginModal from '../../../components/GuestUserLoginModal/GuestUserLoginModal';

import {
  commitPersonalInformation,
  createIndividualApplication,
  makeLoadProfileData,
  updateAnalytics
} from './loaders';
import {
  commitBeneficiaries,
  loadLoginData,
  loadUsersExistingBeneficiaries,
  commitNewInvestmentsBYOP
} from '../shared/loaders';
import {
  commitProfileData,
  commitContactInformation,
  commitReferrerForm,
  loadTermsForSubmission,
  registerAndLoginUser,
  commitShoppingFunds,
  loadMoneyMarketData,
  commitMoneyMarketFund
} from '../../shared/loaders';
import { FlowNode } from '../../../core/types/flow';
import {
  accountTypes,
  contactTextFn,
  redirectToCLX,
  redirectToEnablement
} from '../../shared/utils';
import { updateJourneyProfile } from '../../return-journey/loaders';
import { registerFlow } from '../../../core/flow-register';

export const nodes: Array<FlowNode> = [
  {
    route: 'landing',
    screen: TaxableChecklistScreen,
    load: loadLoginData,
    commit: commitShoppingFunds,
    next: {
      default: routeTo('profile')
    },
    flowProps: {
      displayStickyBanner: true,
      getContactText: contactTextFn(),
      accountType: accountTypes.INDIVIDUAL,
      analytics: {
        pageName: 'account-info'
      }
    }
  },
  {
    route: 'profile',
    screen: TaxableProfileScreen,
    load: makeLoadProfileData(),
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true,
        validation: isValidEmail
      },
      {
        name: 'amount',
        required: true,
        validation: meetsTaxableMinimumInvestment
      }
    ],
    startsPersistingSensitive: true,
    commit: commitProfileData,
    next: {
      cases: [
        [hasURLTickers, routeTo('byop/review')],
        [chose('skipLogin'), routeTo('investment')],
        [guestUserLoginRequired, openModal(GuestUserLoginModal)]
      ],
      default: routeTo('investment')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBasicPI'),
      analytics: {
        pageName: 'About'
      }
    }
  },
  {
    route: 'investment',
    screen: InvestmentOptionsScreen,
    next: {
      cases: [[chose('money-market'), routeTo('money-market')]],
      default: routeTo('byop')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBasicPI'),
      layoutClass: 'wide',
      title:
        'How would you like to choose funds for your general investing account?',
      options: InvestmentOptionsContents,
      analytics: {
        pageName: 'TaxableInvestmentOptions'
      }
    }
  },
  {
    route: 'money-market',
    screen: MoneyMarketScreen,
    load: loadMoneyMarketData,
    commit: commitMoneyMarketFund,
    next: {
      cases: [[chose('viewAllMoneyMarketFunds'), routeTo('byop')]],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'MoneyMarketFund'
      }
    }
  },
  {
    route: 'byop',
    screen: BYOPScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()]
      ],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'BuildPortfolio'
      }
    }
  },
  {
    route: 'byop/review',
    screen: InvestmentReviewScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()],
        [choseResultingPath('reselect-funds'), routeTo('byop')],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('taxableBYOP'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'BuildPortfolioSummary'
      }
    }
  },
  {
    route: 'information',
    screen: PersonalInformationScreen,
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'middleName'
      },
      {
        name: 'dob',
        required: true,
        validation: isValidDob
      },
      {
        name: 'citizenship',
        required: true
      },
      {
        name: 'ssn',
        required: true,
        validation: [isValidMaskedSSN]
      }
    ],
    commit: commitPersonalInformation,
    next: {
      cases: [
        [userValidationFailed, openModal(UserValidationModal)],
        [
          existingUserShouldGotoEnablement,
          openModal(makeForcedLoginModal(redirectToEnablement))
        ],
        [
          existingUserShouldGotoLogin,
          openModal(makeForcedLoginModal(redirectToCLX))
        ],
        [isReviewMode(), routeTo('review')],
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)]
      ],
      default: routeTo('contact/information')
    },
    flowProps: {
      getContactText: contactTextFn('taxablePICI'),
      analytics: {
        pageName: 'PersonalInformation'
      }
    }
  },
  {
    route: 'contact/information',
    screen: ContactInformationScreen,
    load: loadTermsForSubmission,
    form: [
      {
        name: 'residentialStreet1',
        required: true
      },
      {
        name: 'residentialStreet2'
      },
      {
        name: 'residentialZip',
        required: true
      },
      {
        name: 'residentialCity',
        required: true
      },
      {
        name: 'residentialState',
        required: true
      },
      {
        name: 'mailingStreet1',
        required: true
      },
      {
        name: 'mailingStreet2'
      },
      {
        name: 'mailingZip',
        required: true
      },
      {
        name: 'mailingCity',
        required: true
      },
      {
        name: 'mailingState',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true
      },
      {
        name: 'phone1',
        required: true
      },
      {
        name: 'phone2'
      },
      {
        name: 'phone3'
      },
      {
        name: 'mailingAddressDifferent',
        type: 'checkbox'
      },
      {
        name: 'useResidentialAddress',
        required: true
      },
      {
        name: 'useMailingAddress',
        required: true
      }
    ],
    commit: commitContactInformation,
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isReviewMode(), routeTo('review')],
        [isLAResidentRouteTester, routeTo('credentials')]
      ],
      default: routeTo('beneficiaries')
    },
    flowProps: {
      getContactText: contactTextFn('taxablePICI'),
      analytics: {
        pageName: 'ContactInformation'
      }
    }
  },
  {
    route: 'beneficiaries',
    screen: BeneficiariesScreen,
    commit: commitBeneficiaries,
    load: loadUsersExistingBeneficiaries,
    next: {
      cases: [
        [
          chose('dangerouslySkipValidation:open-modal'),
          openModal(SkipBeneficiariesModal)
        ],
        [isReviewMode(), routeTo('review')],
        [isAuthenticated(), routeTo('review')]
      ],
      default: routeTo('credentials')
    },
    form: [
      {
        name: 'beneficiaryType'
      },
      {
        name: 'entityName',
        required: true
      },
      {
        name: 'beneFirstName',
        required: true
      },
      {
        name: 'beneMiddleName',
        required: false
      },
      {
        name: 'beneLastName',
        required: true
      },
      {
        name: 'beneSuffix',
        required: false,
        type: 'select'
      },
      {
        name: 'beneRelationship',
        required: true,
        type: 'select'
      },
      {
        name: 'beneDateOfBirth',
        required: false,
        validation: isValidBeneorSucessorDob
      },
      {
        name: 'beneTaxID',
        required: false,
        validation: [isValidSSN]
      },
      {
        name: 'percentage',
        type: 'number'
      }
    ],
    flowProps: {
      getContactText: contactTextFn('taxableBeneficiary'),
      title: 'Would you like to add beneficiaries?',
      analytics: {
        pageName: 'Beneficiaries'
      }
    }
  },
  {
    route: 'credentials',
    form: [
      {
        name: 'username',
        required: true,
        validation: usernameValidation
      },
      {
        name: 'password',
        required: true,
        validation: passwordValidation
      }
    ],
    screen: CreateCredentialsScreen,
    commit: registerAndLoginUser,
    next: {
      cases: [[isUsernameAlreadyTaken, openModal(CredentialsValidationModal)]],
      default: routeTo('review')
    },
    flowProps: {
      getContactText: contactTextFn('taxableCredentials'),
      analytics: {
        pageName: 'Enablement'
      }
    }
  },
  {
    route: 'review',
    screen: ReviewScreen,
    load: loadTermsForSubmission,
    commit: createIndividualApplication,
    next: {
      cases: [
        [chose('updateCustomer'), partiallyUpdate()],
        [chose('byop'), routeTo('byop')],
        [chose('info'), routeTo('information')],
        [chose('contact'), routeTo('contact/information')],
        [chose('beneficiaries'), routeTo('beneficiaries')]
      ],
      default: routeTo('confirmation')
    },
    form: [
      {
        name: 'eligibleForTaxWithholding'
      }
    ],
    flowProps: {
      renderTaxpayerCertifications: true,
      getContactText: contactTextFn('taxableReview'),
      analytics: {
        pageName: 'ReviewConfirm'
      }
    }
  },
  {
    route: 'confirmation',
    screen: ConfirmationScreen,
    commit: commitReferrerForm,
    next: {
      cases: [[chose('referrer'), partiallyUpdate()]],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'Confirmation',
        isComplete: true
      }
    }
  }
];

export default registerFlow({
  namespace: `mutual-fund/general-investing/individual`,
  nodes,
  start: 'landing',
  pageDecorators: [BaseLayout, SummitRewards],
  onLoad: updateAnalytics,
  onCommit: updateJourneyProfile,
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
